import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchAvailabilityRules, 
  createAvailabilityRule, 
  updateAvailabilityRule, 
  deleteAvailabilityRule,
  toggleAvailabilityRule,
  importAvailabilityRules,
  AvailabilityRulesResponse 
} from '@/api/availability-rules';
import { AvailabilityRule } from '@/types/revenue-manager';

export function useAvailabilityRules(propertyId: string) {
  return useQuery<AvailabilityRulesResponse>({
    queryKey: ['availability-rules', propertyId],
    queryFn: () => fetchAvailabilityRules(propertyId),
    enabled: !!propertyId,
  });
}

export function useCreateAvailabilityRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (rule: Omit<AvailabilityRule, 'id' | 'createdAt' | 'updatedAt'>) => 
      createAvailabilityRule(propertyId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules', propertyId] });
    },
  });
}

export function useUpdateAvailabilityRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ruleId, updates }: { ruleId: string; updates: Partial<AvailabilityRule> }) => 
      updateAvailabilityRule(propertyId, ruleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules', propertyId] });
    },
  });
}

export function useDeleteAvailabilityRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ruleId: string) => deleteAvailabilityRule(propertyId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules', propertyId] });
    },
  });
}

export function useToggleAvailabilityRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) => 
      toggleAvailabilityRule(propertyId, ruleId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules', propertyId] });
    },
  });
}

export function useImportAvailabilityRules() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ targetPropertyIds, rules }: { 
      targetPropertyIds: string[]; 
      rules: Omit<AvailabilityRule, 'id' | 'createdAt' | 'updatedAt'>[] 
    }) => importAvailabilityRules(targetPropertyIds, rules),
    onSuccess: (_, variables) => {
      // Invalidate all affected properties
      variables.targetPropertyIds.forEach(propertyId => {
        queryClient.invalidateQueries({ queryKey: ['availability-rules', propertyId] });
      });
    },
  });
}
